/**
 * AnnuairePartenaires.jsx — Mini back-office partenaires annuaire.
 *
 * Permet à l'administrateur de modifier EN 10 SECONDES la bannière promo
 * (ex: "Prochaine Formation B96 : 2 avril 2026") affichée sur toutes les
 * fiches `featured_partner: True` d'un département.
 *
 * Workflow :
 *   1. Sélectionner le département (défaut : 01)
 *   2. Charger la bannière actuelle via GET
 *   3. Éditer les 5 champs (texte, sous-titre, bouton, URL, couleur)
 *   4. Un clic sur "Appliquer" met à jour les N fiches en bulk via PUT
 *   5. Bouton "Masquer la bannière" pour $unset partout
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Megaphone, Loader2, CheckCircle2, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import api from '../../api';

const DEFAULT_DEPT = '01';

export default function AnnuairePartenaires() {
  const [dept, setDept] = useState(DEFAULT_DEPT);
  const [partnersCount, setPartnersCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState({
    text: '',
    subtitle: '',
    cta_label: '',
    cta_url: '',
    bg_color: 'red',
  });

  const load = async (d = dept) => {
    setLoading(true);
    try {
      const data = await api.adminGetPartnersPromoBanner(d);
      setPartnersCount(data.partners_count || 0);
      setBanner({
        text: data.promo_banner?.text || '',
        subtitle: data.promo_banner?.subtitle || '',
        cta_label: data.promo_banner?.cta_label || '',
        cta_url: data.promo_banner?.cta_url || '',
        bg_color: data.promo_banner?.bg_color || 'red',
      });
    } catch (e) {
      toast.error(`Erreur chargement : ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { document.title = 'Partenaires annuaire · Admin'; load(DEFAULT_DEPT); /* eslint-disable-next-line */ }, []);

  const handleSave = async () => {
    if (!banner.text.trim()) {
      toast.error('Le texte principal est obligatoire.');
      return;
    }
    setSaving(true);
    try {
      const res = await api.adminUpdatePartnersPromoBanner(dept, banner);
      toast.success(`✅ Bannière appliquée sur ${res.modified} fiche${res.modified > 1 ? 's' : ''}`);
    } catch (e) {
      toast.error(`Erreur sauvegarde : ${e?.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm(`Masquer la bannière sur les ${partnersCount} fiche(s) partenaire(s) du ${dept} ?`)) return;
    setSaving(true);
    try {
      const res = await api.adminUpdatePartnersPromoBanner(dept, null);
      toast.success(`🧹 Bannière masquée sur ${res.modified} fiche${res.modified > 1 ? 's' : ''}`);
      setBanner({ text: '', subtitle: '', cta_label: '', cta_url: '', bg_color: 'red' });
    } catch (e) {
      toast.error(`Erreur : ${e?.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const bgPreviewClass = {
    red: 'from-red-600 via-red-500 to-red-600 border-red-400/40',
    amber: 'from-amber-600 via-amber-500 to-amber-600 border-amber-400/40',
    emerald: 'from-emerald-600 via-emerald-500 to-emerald-600 border-emerald-400/40',
    blue: 'from-blue-600 via-blue-500 to-blue-600 border-blue-400/40',
  }[banner.bg_color] || 'from-red-600 via-red-500 to-red-600 border-red-400/40';

  return (
    <div className="min-h-screen bg-black text-white p-4 sm:p-8">
      <div className="max-w-4xl mx-auto">
        <Link to="/admin/annuaire" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white mb-4" data-testid="back-to-annuaire">
          <ArrowLeft className="w-4 h-4" /> Retour annuaire
        </Link>

        <header className="mb-6">
          <h1 className="text-3xl font-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Bannière promo partenaires
          </h1>
          <p className="text-white/60 mt-1">
            Modifiez en 10 secondes la bannière rouge affichée en haut des fiches partenaires du département.
          </p>
        </header>

        {/* Sélecteur département */}
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 mb-6">
          <div className="flex items-center gap-3 flex-wrap">
            <Label className="text-white/70">Département :</Label>
            <Input
              value={dept}
              onChange={(e) => setDept(e.target.value.replace(/\D/g, '').slice(0, 3))}
              className="w-20 bg-black/40 border-white/20"
              data-testid="dept-input"
            />
            <Button variant="outline" onClick={() => load(dept)} disabled={loading} data-testid="dept-load-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Charger
            </Button>
            <span className="text-white/60 text-sm ml-auto">
              <strong className="text-amber-300">{partnersCount}</strong> fiche{partnersCount > 1 ? 's' : ''} partenaire{partnersCount > 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Preview live de la bannière */}
        <div className="mb-6" data-testid="banner-preview">
          <div className="text-xs text-white/50 uppercase tracking-widest mb-2">Aperçu live</div>
          <div className={`rounded-xl overflow-hidden border-2 ${bgPreviewClass} shadow-lg`}>
            <div className={`flex items-center justify-between gap-3 px-5 py-3 bg-gradient-to-r ${bgPreviewClass}`}>
              <div className="flex items-center gap-2.5 min-w-0 flex-1">
                <Megaphone className="w-5 h-5 text-white shrink-0 animate-pulse" />
                <div className="min-w-0">
                  <div className="text-white font-black text-sm uppercase tracking-wide leading-tight">
                    {banner.text || '— Texte principal (ex: PROCHAINE FORMATION B96 : 2 AVRIL 2026) —'}
                  </div>
                  {banner.subtitle && <div className="text-white/90 text-xs leading-tight">{banner.subtitle}</div>}
                </div>
              </div>
              {banner.cta_label && (
                <span className="shrink-0 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white text-red-700 text-[10.5px] font-black uppercase tracking-widest">
                  {banner.cta_label} →
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Formulaire */}
        <div className="space-y-4 rounded-xl border border-white/10 bg-white/[0.02] p-5">
          <div>
            <Label htmlFor="banner-text" className="text-white/80">Texte principal <span className="text-red-400">*</span></Label>
            <Input
              id="banner-text"
              value={banner.text}
              onChange={(e) => setBanner({ ...banner, text: e.target.value })}
              placeholder="PROCHAINE FORMATION B96 : 2 AVRIL 2026"
              className="bg-black/40 border-white/20 mt-1"
              data-testid="input-text"
            />
          </div>

          <div>
            <Label htmlFor="banner-subtitle" className="text-white/80">Sous-titre</Label>
            <Input
              id="banner-subtitle"
              value={banner.subtitle}
              onChange={(e) => setBanner({ ...banner, subtitle: e.target.value })}
              placeholder="Permis remorque — Places limitées"
              className="bg-black/40 border-white/20 mt-1"
              data-testid="input-subtitle"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="banner-cta-label" className="text-white/80">Texte du bouton</Label>
              <Input
                id="banner-cta-label"
                value={banner.cta_label}
                onChange={(e) => setBanner({ ...banner, cta_label: e.target.value })}
                placeholder="Je m'inscris"
                className="bg-black/40 border-white/20 mt-1"
                data-testid="input-cta-label"
              />
            </div>
            <div>
              <Label htmlFor="banner-cta-url" className="text-white/80">URL du bouton</Label>
              <Input
                id="banner-cta-url"
                value={banner.cta_url}
                onChange={(e) => setBanner({ ...banner, cta_url: e.target.value })}
                placeholder="https://www.cer2lain.com/formationb96cerbyfrancine.html"
                className="bg-black/40 border-white/20 mt-1"
                data-testid="input-cta-url"
              />
            </div>
          </div>

          <div>
            <Label className="text-white/80">Couleur de la bannière</Label>
            <div className="flex gap-2 mt-2" data-testid="color-picker">
              {['red', 'amber', 'emerald', 'blue'].map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setBanner({ ...banner, bg_color: c })}
                  className={`w-10 h-10 rounded-lg border-2 transition ${
                    banner.bg_color === c ? 'border-white scale-110' : 'border-white/20 hover:border-white/50'
                  } ${
                    c === 'red' ? 'bg-red-500' : c === 'amber' ? 'bg-amber-500' : c === 'emerald' ? 'bg-emerald-500' : 'bg-blue-500'
                  }`}
                  data-testid={`color-${c}`}
                  aria-label={c}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6 flex-wrap">
          <Button
            onClick={handleSave}
            disabled={saving || !banner.text.trim()}
            className="h-11 px-6 bg-amber-500 hover:bg-amber-400 text-black font-black uppercase tracking-wide"
            data-testid="save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Appliquer sur {partnersCount} fiche{partnersCount > 1 ? 's' : ''}
          </Button>
          <Button
            onClick={handleClear}
            variant="outline"
            disabled={saving || !partnersCount}
            className="h-11 px-5 border-red-500/40 text-red-300 hover:bg-red-500/10 hover:text-red-200"
            data-testid="clear-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Masquer la bannière
          </Button>
        </div>

        <p className="mt-6 text-xs text-white/40 leading-relaxed">
          Les modifications sont appliquées immédiatement sur toutes les fiches
          <code className="mx-1 px-1 py-0.5 bg-white/5 rounded">featured_partner: true</code>
          du département sélectionné. L'affichage côté public se met à jour au prochain chargement de la page.
        </p>
      </div>
    </div>
  );
}
